import { useEffect, useState } from "react";
import {
  ArrowLeftIcon,
  BellIcon,
  CalendarBlankIcon,
  CheckCircleIcon,
  ClockIcon,
  FlagIcon,
  HeartIcon,
  HouseIcon,
  ListIcon,
  MapPinIcon,
  PawPrintIcon,
  PaperPlaneTiltIcon,
  PhoneIcon,
  PlusIcon,
  ShareNetworkIcon,
  ShieldCheckIcon,
  UserCircleIcon,
  UserIcon,
  WhatsappLogoIcon,
} from "@phosphor-icons/react";
import { api } from "./lib/api.js";
import { fechaLegible, horaLegible, numeroContacto } from "./lib/detalleReporte.js";
import { MapaUbicacion } from "./UbicacionMascotaForm.jsx";
import HistoriaDesdePerfil from "./HistoriaDesdePerfil.jsx";
import ReportarAnuncioModal from "./ReportarAnuncioModal.jsx";

const etiquetas = {
  PERRO: "Perro", GATO: "Gato", OTRO: "Otra mascota",
  MACHO: "Macho", HEMBRA: "Hembra",
  MUY_PEQUENO: "Muy pequeño", PEQUENO: "Pequeño", MEDIANO: "Mediano", GRANDE: "Grande", MUY_GRANDE: "Muy grande",
  NEGRO: "Negro", BLANCO: "Blanco", MARRON: "Marrón", CAFE_CLARO: "Café claro", BEIGE: "Beige", CREMA: "Crema",
  DORADO: "Dorado", AMARILLO: "Amarillo", ROJIZO: "Rojizo", GRIS: "Gris", CANELA: "Canela", ATIGRADO: "Atigrado", MANCHADO: "Manchado",
};

function texto(valor, alternativo = "Sin especificar") {
  return etiquetas[valor] ?? alternativo;
}

export function NavegacionDetalle({ onBack, onNavigate, vistaReportar = "mascota-perdida" }) {
  return <nav className="pet-detail-nav" aria-label="Navegación principal"><button type="button" onClick={() => onNavigate("inicio")}><HouseIcon /><span>Inicio</span></button><button className="is-active" type="button" onClick={onBack}><ListIcon weight="fill" /><span>Reportes</span></button><button type="button" onClick={() => onNavigate(vistaReportar)}><span className="pet-detail-nav__add"><PlusIcon weight="bold" /></span><span>Reportar</span></button><button type="button"><BellIcon /><span>Alertas</span></button><button type="button"><UserCircleIcon /><span>Mi cuenta</span></button></nav>;
}

export default function DetalleMascota({ id, onBack, onNavigate }) {
  const [reporte, setReporte] = useState(null);
  const [estado, setEstado] = useState("cargando");
  const [error, setError] = useState("");
  const [fotoActiva, setFotoActiva] = useState(0);
  const [favorito, setFavorito] = useState(() => localStorage.getItem(`encuentrayayuda:favorito:${id}`) === "1");
  /// Confirmacion de 'compartir': sin esto la accion no daba ninguna senal
  /// y no se sabia si habia pasado algo.
  const [avisoCompartir, setAvisoCompartir] = useState('');
  const [verMas, setVerMas] = useState(false);
  const [mostrarHistoria, setMostrarHistoria] = useState(false);
  const [mostrarDenuncia, setMostrarDenuncia] = useState(false);

  useEffect(() => {
    let vigente = true;
    setEstado("cargando");
    setFotoActiva(0);
    api.mascotas.detalle(id)
      .then((datos) => { if (vigente) { setReporte(datos); setEstado("listo"); } })
      .catch((err) => { if (vigente) { setError(err.message); setEstado("error"); } });
    return () => { vigente = false; };
  }, [id]);

  const fotos = reporte?.multimedia?.filter((medio) => medio.tipo === "FOTO") ?? [];
  const evento = reporte?.tipo === "ENCONTRADO" ? reporte?.encontradoEn : reporte?.perdidoEn;
  const punto = reporte?.tipo === "ENCONTRADO" ? reporte?.ubicacion?.encontro : reporte?.ubicacion?.perdido;
  const whatsapp = reporte?.contacto?.whatsapp ?? "";
  const telefono = numeroContacto(whatsapp);

  function alternarFavorito() {
    const siguiente = !favorito;
    setFavorito(siguiente);
    if (siguiente) localStorage.setItem(`encuentrayayuda:favorito:${id}`, "1");
    else localStorage.removeItem(`encuentrayayuda:favorito:${id}`);
  }

  async function compartir() {
    const datos = { title: `Ayuda a encontrar a ${reporte?.nombreMascota || "esta mascota"}`, text: reporte?.caracteristicasParticulares || "Comparte este reporte.", url: window.location.href };
    // navigator.share ya muestra la hoja del sistema: ahi el usuario ve el
    // resultado. El respaldo de portapapeles es silencioso, y ese es el caso
    // que necesita confirmacion explicita.
    try {
      if (navigator.share) {
        await navigator.share(datos);
        return;
      }
      await navigator.clipboard.writeText(window.location.href);
      mostrarAviso("Enlace copiado");
    } catch (error) {
      // AbortError = el usuario cerro la hoja de compartir a proposito.
      if (error?.name === "AbortError") return;
      mostrarAviso("No se pudo compartir. Copia la dirección de la barra.");
    }
  }

  function mostrarAviso(texto) {
    setAvisoCompartir(texto);
    setTimeout(() => setAvisoCompartir(""), 2600);
  }

  if (estado !== "listo") {
    return <main className="pet-detail-state"><button type="button" onClick={onBack}><ArrowLeftIcon weight="bold" />Volver</button>{estado === "cargando" ? <><span className="loader" /><p>Cargando el perfil…</p></> : <><PawPrintIcon weight="duotone" /><h1>No pudimos cargar el perfil</h1><p>{error}</p></>}</main>;
  }

  const perdida = reporte.tipo !== "ENCONTRADO";
  const nombre = reporte.nombreMascota || "Mascota sin identificar";

  return <div className="pet-detail-page">
    <header className="pet-detail-header"><button type="button" onClick={onBack} aria-label="Volver"><ArrowLeftIcon weight="bold" /></button><h1>Detalle de la mascota</h1><div><button type="button" onClick={compartir} aria-label="Compartir reporte"><ShareNetworkIcon weight="bold" /></button><button type="button" className={favorito ? "is-favorite" : ""} onClick={alternarFavorito} aria-label={favorito ? "Quitar de favoritos" : "Guardar en favoritos"}><HeartIcon weight={favorito ? "fill" : "bold"} /></button></div></header>
      {avisoCompartir && <p className="aviso-compartir" role="status">{avisoCompartir}</p>}

    <main className="pet-detail-content">
      <div className="pet-detail-meta"><span><PawPrintIcon weight="fill" />{texto(reporte.tipoMascota)} {perdida ? "perdido" : "encontrado"}</span><time dateTime={reporte.createdAt}><CalendarBlankIcon />Reportado el {fechaLegible(reporte.createdAt)}</time></div>

      <section className="pet-gallery" aria-label={`Fotos de ${nombre}`}>
        <div className="pet-gallery__main">{fotos[fotoActiva] ? <><img className="pet-gallery__backdrop" src={fotos[fotoActiva].url} alt="" aria-hidden="true" /><img className="pet-gallery__photo" src={fotos[fotoActiva].url} alt={`Fotografía ${fotoActiva + 1} de ${nombre}`} /></> : <PawPrintIcon weight="fill" />}<span>{fotos.length ? `${fotoActiva + 1}/${fotos.length}` : "Sin fotos"}</span></div>
        {fotos.length > 1 && <div className="pet-gallery__thumbs">{fotos.map((foto, indice) => <button type="button" className={fotoActiva === indice ? "is-active" : ""} onClick={() => setFotoActiva(indice)} aria-label={`Ver fotografía ${indice + 1}`} key={foto.id}><img src={foto.urlThumb || foto.url} alt="" /></button>)}</div>}
      </section>

      <section className="pet-detail-card pet-detail-summary"><h2>{nombre}<CheckCircleIcon weight="fill" /></h2><div className="pet-detail-tags"><span><UserIcon />{texto(reporte.sexo)}</span><span><PawPrintIcon />{texto(reporte.tamano)}</span><span><PawPrintIcon />{texto(reporte.tipoMascota)}</span><span><PawPrintIcon />{texto(reporte.colorPrincipalPelaje)}</span></div><p>{reporte.caracteristicasParticulares || "No se registraron características particulares."}</p>{reporte.puntoReferencia && <button type="button" onClick={() => setVerMas((valor) => !valor)}>{verMas ? "Ocultar información" : "Ver más características"}</button>}{verMas && <p className="pet-detail-summary__extra">Referencia adicional: {reporte.puntoReferencia}</p>}</section>

      <section className="pet-detail-card pet-detail-location"><div><MapPinIcon weight="fill" /><div><strong>{perdida ? "Última vez visto" : "Lugar del hallazgo"}</strong><p>{reporte.direccionHallazgo || "Ubicación registrada en el mapa"}</p></div></div>{punto && <div className="pet-detail-map"><MapaUbicacion latitud={punto.lat} longitud={punto.lng} precision={null} onLocation={() => {}} etiqueta="Ubicación aproximada de la mascota" /></div>}<div className="pet-detail-date"><span><CalendarBlankIcon weight="bold" /><strong>{fechaLegible(evento)}</strong><small>Fecha</small></span><span><ClockIcon weight="bold" /><strong>{horaLegible(evento)}</strong><small>Hora aproximada</small></span></div></section>

      <section className="pet-detail-card pet-detail-reporter"><div><small>Reportado por</small><strong><UserIcon weight="duotone" />{reporte.contacto?.nombre || "Usuario de la comunidad"}</strong></div>{whatsapp && <a href={whatsapp} target="_blank" rel="noreferrer" aria-label="Contactar por WhatsApp"><WhatsappLogoIcon weight="fill" /></a>}</section>

      <button className="story-create-button" type="button" onClick={() => setMostrarHistoria(true)}><PaperPlaneTiltIcon weight="fill" /><span><strong>Crear imagen para historias</strong><small>Genera un afiche vertical listo para compartir</small></span></button>

      <aside className="pet-detail-trust"><ShieldCheckIcon weight="duotone" /><div><strong>Reporte activo</strong><p>Si tienes información que pueda ayudar a encontrar a {nombre}, contacta a la persona que reportó.</p></div></aside>

      <button className="report-listing-button" type="button" onClick={() => setMostrarDenuncia(true)}><FlagIcon weight="bold" />Reportar este anuncio</button>

      {whatsapp && <div className="pet-detail-actions"><a href={whatsapp} target="_blank" rel="noreferrer"><WhatsappLogoIcon weight="fill" />Contactar por WhatsApp</a>{telefono && <a href={`tel:+${telefono}`}><PhoneIcon weight="fill" />Llamar</a>}</div>}
    </main>

    <NavegacionDetalle onBack={onBack} onNavigate={onNavigate} />
    {mostrarHistoria && <HistoriaDesdePerfil entidad="mascota" reporte={reporte} onClose={() => setMostrarHistoria(false)} />}
    {mostrarDenuncia && <ReportarAnuncioModal entidad="mascota" reporteId={reporte.id} onClose={() => setMostrarDenuncia(false)} />}
  </div>;
}
